import { IconChevronLeft, IconChevronRight, IconDots } from "@tabler/icons-react";
import * as React from "react";

import { Button } from "@beweegbaas/ui/components/ui/button";
import { Link } from "@beweegbaas/ui/components/ui/link";
import { cn } from "@beweegbaas/ui/lib/utils";

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className={cn("flex justify-between gap-6", className)}
      {...props}
    />
  );
}

function PaginationInfo({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "text-muted-foreground flex items-center justify-center gap-1 text-xs",
        className,
      )}
      {...props}
    />
  );
}

function PaginationList({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      className={cn("flex flex-row items-center gap-1 rounded-sm border p-[2px]", className)}
      {...props}
    />
  );
}

function PaginationItem({ className, ...props }: React.ComponentProps<"li">) {
  return <li className={cn("flex items-center", className)} {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
  disabled?: boolean;
  href?: string;
} & Omit<React.ComponentProps<typeof Button>, "render" | "nativeButton" | "variant" | "size"> & {
    size?: "default" | "sm" | "lg" | "icon";
  };

function PaginationLink({
  className,
  isActive,
  disabled,
  size = "sm",
  href,
  ...props
}: PaginationLinkProps) {
  const shouldRenderAsLink = !disabled && href;

  return (
    <Button
      variant={isActive ? "secondary" : "ghost"}
      size={size}
      disabled={disabled}
      className={cn("h-6 w-6 rounded-sm p-0", className)}
      render={
        shouldRenderAsLink ? (
          <Link variant={"secondary"} href={href} aria-current={isActive ? "page" : undefined} />
        ) : undefined
      }
      nativeButton={!shouldRenderAsLink}
      {...props}
    />
  );
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn("", className)}
      {...props}
    >
      <IconChevronLeft className="h-4 w-4" />
    </PaginationLink>
  );
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn("cursor-pointer", className)}
      {...props}
    >
      <IconChevronRight className="h-4 w-4" />
    </PaginationLink>
  );
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<typeof PaginationItem>) {
  return (
    <PaginationItem
      aria-hidden
      className={cn("flex justify-center self-end opacity-30", className)}
      {...props}
    >
      <IconDots className="h-4 w-4" />
      <span className="sr-only">More pages</span>
    </PaginationItem>
  );
}

// Add compound components
Pagination.List = PaginationList;
Pagination.Item = PaginationItem;
Pagination.Link = PaginationLink;
Pagination.Previous = PaginationPrevious;
Pagination.Next = PaginationNext;
Pagination.Ellipsis = PaginationEllipsis;
Pagination.Section = PaginationList;
Pagination.Info = PaginationInfo;

export { Pagination };
